import { ArrowUpRight, Footprints, MapPin } from "lucide-react";
import { ChapterHeading, Reveal } from "./Reveal";
import { GMB_URL, CALL_URL, PHONE_DISPLAY } from "./ContactButtons";

const LOCATION_IMG =
  "https://images.unsplash.com/photo-1519955266818-0231b63402bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxoZXJpdGFnZSUyMGluZGlhbiUyMGFyY2hpdGVjdHVyZSUyMGludGVyaW9yfGVufDB8fHx8MTc4NjEyNjA1OXww&ixlib=rb-4.1.0&q=85";

const MAP_EMBED =
  "https://www.google.com/maps?q=Pipewalas%20House%20%26%20Homestay%2C%20Thatheri%20Bazar%2C%20Varanasi%2C%20Uttar%20Pradesh%20221001&output=embed";

const LANDMARKS = [
  { name: "Dashashwamedh Ghat", detail: "12-minute walk — the evening Ganga aarti", time: "12 min" },
  { name: "Kashi Vishwanath Temple", detail: "The golden temple of Shiva", time: "~1 km" },
  { name: "Manikarnika Ghat", detail: "The eternal cremation ghat", time: "~1 km" },
  { name: "Thatheri Bazar", detail: "The brass metalworkers' bazaar — you're in it", time: "0 min" },
];

export const Location = () => (
  <section id="location" data-testid="location-section" className="border-t border-white/5 bg-[#0b0b0d]">
    <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <ChapterHeading number="03" label="The Sacred Mile" testId="location-chapter" />

      <div className="grid gap-16 md:grid-cols-12">
        <div className="md:col-span-5">
          <Reveal>
            <h2 className="font-serif text-4xl leading-[1.05] tracking-tight text-[#F5F5F0] sm:text-5xl lg:text-6xl">
              Everything holy is a <em className="font-light italic text-[#C5A059]">short walk</em> away.
            </h2>
          </Reveal>

          <div className="mt-14 flex flex-col">
            {LANDMARKS.map((l, i) => (
              <Reveal key={l.name} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`landmark-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-start justify-between gap-6 border-t border-white/5 py-6 transition-colors duration-500 hover:border-[#C5A059]/30"
                >
                  <div>
                    <p className="font-serif text-xl text-[#F5F5F0] md:text-2xl">{l.name}</p>
                    <p className="mt-1 text-sm text-[#A1A1AA]">{l.detail}</p>
                  </div>
                  <span className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-[#C5A059]">
                    <Footprints className="h-4 w-4" />
                    {l.time}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-5 md:col-span-6 md:col-start-7">
          <Reveal delay={0.15} className="group relative overflow-hidden rounded-2xl">
            <img
              src={LOCATION_IMG}
              alt="Temple architecture of Varanasi"
              className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080809]/70 to-transparent" />
          </Reveal>

          <Reveal delay={0.25} className="overflow-hidden rounded-2xl border border-white/5">
            <iframe
              data-testid="location-map"
              title="Pipewalas House & Homestay on Google Maps"
              src={MAP_EMBED}
              className="h-72 w-full grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>

          <Reveal delay={0.3}>
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/5 bg-[#121214] p-6">
              <div className="flex items-start gap-3">
                <MapPin className="mt-1 h-4 w-4 shrink-0 text-[#C5A059]" />
                <div>
                  <p data-testid="location-address" className="text-sm leading-relaxed text-[#A1A1AA]">
                    CK21/35-37, Thatheri Bazar, Govindpura,
                    <br />
                    Varanasi, Uttar Pradesh 221001
                  </p>
                  <a
                    data-testid="location-phone-link"
                    href={CALL_URL}
                    className="mt-2 inline-block text-sm font-semibold text-[#F5F5F0] transition-colors hover:text-[#C5A059]"
                  >
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
              <a
                data-testid="location-gmb-link"
                href={GMB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#C5A059] transition-colors hover:text-[#D4AF37]"
              >
                Open in Google Maps
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
